import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import {
  ShieldCheck, BadgeCheck, Check, TrendingUp, Zap, ArrowRight, Loader2, Award, Globe,
  FileText, QrCode, Search, Briefcase, ChevronDown, Fingerprint, Brain, ScanLine, Rocket,
  X as XIcon, Plane, MapPin
} from 'lucide-react';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;
const RUBY = '#B91C1C';
const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); };
const head = { fontFamily: "'Space Grotesk', sans-serif" };

const VerifiedTalentLandingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [threshold, setThreshold] = useState(30);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMap, setCouponMap] = useState({});
  const [couponMsg, setCouponMsg] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    document.title = 'EvalentX Verified Profile | Get Verified. Get Discovered. Get Hired.';
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); return m;
    })();
    meta.content = 'Get your profile verified and unlock multiple interview opportunities across India and abroad. Demonstrate your skills, generate a recruiter-ready resume and certificate, and get discovered.';
    fetchData(); // eslint-disable-next-line
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, meRes] = await Promise.all([
        axios.get(`${API}/api/verified-talent/plans`),
        token ? axios.get(`${API}/api/verified-talent/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null) : Promise.resolve(null),
      ]);
      setPlans(plansRes.data.plans || []);
      setThreshold(plansRes.data.threshold || 30);
      if (meRes?.data) setMe(meRes.data);
    } catch { toast.error('Failed to load plans'); } finally { setLoading(false); }
  };

  const loadCashfreeSdk = () => new Promise((resolve) => {
    if (window.Cashfree) return resolve();
    const s = document.createElement('script'); s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js'; s.onload = resolve; document.body.appendChild(s);
  });

  const handlePurchase = async (planId) => {
    if (!token) { toast.info('Please log in first'); navigate(loginUrlWithRedirect('/codearena/auth', '/verified-talent')); return; }
    setPurchasing(planId);
    try {
      const res = await axios.post(`${API}/api/verified-talent/purchase`, { plan_id: planId, coupon_code: appliedCoupon || undefined }, { headers: { Authorization: `Bearer ${token}` } });
      const { payment_session_id, cashfree_env, free } = res.data;
      if (free) { toast.success('Free Trial activated! Start your verification now.'); setPurchasing(null); navigate(`/verified-talent/verify?order_id=${res.data.order_id}`); return; }
      if (!payment_session_id) { toast.error('Payment session could not be created'); setPurchasing(null); return; }
      await loadCashfreeSdk();
      // eslint-disable-next-line no-undef
      const cashfree = window.Cashfree({ mode: cashfree_env === 'sandbox' ? 'sandbox' : 'production' });
      cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: '_self', returnUrl: `${window.location.origin}/verified-talent/verify?order_id=${res.data.order_id}` });
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start checkout'); setPurchasing(null); }
  };

  const hasActivePlan = me?.has_active_plan && (me?.profile?.attempts_remaining > 0);
  const isVerified = me?.profile?.is_verified;
  const primaryCta = () => { if (isVerified || hasActivePlan) navigate('/verified-talent/verify'); else scrollTo('plans'); };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (!token) { toast.info('Please log in to apply a coupon'); navigate(loginUrlWithRedirect('/codearena/auth', '/verified-talent')); return; }
    setApplyingCoupon(true); setCouponMsg(null);
    try {
      const paid = plans.filter((p) => Number(p.price) > 0);
      const results = await Promise.all(paid.map((p) =>
        axios.post(`${API}/api/verified-talent/coupons/validate`, { code, plan_id: p.id }, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => [p.id, r.data]).catch(() => [p.id, { valid: false }])
      ));
      const map = Object.fromEntries(results);
      const anyValid = results.some(([, v]) => v.valid);
      setCouponMap(map);
      if (anyValid) { setAppliedCoupon(code); setCouponMsg({ ok: true, text: `Coupon "${code}" applied!` }); }
      else {
        const reason = (results.find(([, v]) => v.reason) || [])[1]?.reason || 'This coupon is not valid';
        setAppliedCoupon(null); setCouponMsg({ ok: false, text: reason });
      }
    } catch { setCouponMsg({ ok: false, text: 'Could not validate coupon' }); }
    finally { setApplyingCoupon(false); }
  };
  const clearCoupon = () => { setAppliedCoupon(null); setCouponMap({}); setCouponMsg(null); setCouponInput(''); };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="verified-talent-landing" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ================= HERO ================= */}
      <section className="relative border-b border-[#F5F5F5] bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_#FEF2F2,_transparent_45%)]" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FEF2F2] border border-[#FECACA] rounded-full text-xs font-bold text-[#B91C1C] mb-6">
              <ShieldCheck size={12} /> Your verified career identity
            </span>
            <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] mb-5" style={head}>
              Get Verified.<br />Get Discovered.<br /><span className="text-[#B91C1C]">Get Hired.</span>
            </h1>
            <p className="text-base md:text-lg text-[#525252] max-w-xl mb-6 leading-relaxed">
              Turn your skills, experience and capabilities into a verified professional profile — and unlock <strong className="text-[#0A0A0A]">multiple interview opportunities across India and abroad.</strong>
            </p>
            {/* India + abroad promise strip */}
            <div className="inline-flex flex-wrap items-center gap-3 mb-8 px-4 py-2.5 rounded-2xl bg-[#0A0A0A] text-white text-sm font-semibold" data-testid="interview-guarantee-strip">
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#B91C1C]" /> India</span>
              <span className="text-white/30">+</span>
              <span className="inline-flex items-center gap-1.5"><Plane size={14} className="text-[#B91C1C]" /> Abroad</span>
              <span className="text-white/30">·</span>
              <span>Multiple guaranteed interview opportunities</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={primaryCta} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#B91C1C] hover:bg-[#7F1D1D] text-white font-bold text-sm transition-colors" data-testid="hero-get-verified-btn">
                Get My Profile Verified <ArrowRight size={16} />
              </button>
              <button onClick={() => scrollTo('how')} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#E5E5E5] hover:border-[#B91C1C] font-bold text-sm text-[#0A0A0A]" data-testid="hero-how-btn">
                See How It Works
              </button>
              <Link to="/verified-developers" className="inline-flex items-center gap-2 px-4 py-3.5 text-sm font-bold text-[#525252] hover:text-[#B91C1C]">
                Explore Opportunities
              </Link>
            </div>
          </div>

          {/* Sample Verified Profile Card */}
          <div className="relative">
            <div className="mx-auto max-w-sm rounded-3xl border border-[#E5E5E5] bg-white p-6 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.10)]" data-testid="sample-profile-card">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-[#B91C1C] flex items-center justify-center text-xl font-extrabold text-white">RK</div>
                <div>
                  <p className="font-extrabold text-lg leading-tight text-[#0A0A0A]" style={head}>Rahul Kumar</p>
                  <p className="text-xs text-[#525252]">Software Engineer · AI & Full Stack</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {['Identity Verified', 'Skills Verified', 'Assessment Verified', 'Profile Verified'].map((v) => (
                  <div key={v} className="flex items-center gap-1.5 text-[11px] font-semibold text-[#B91C1C]"><BadgeCheck size={13} /> {v}</div>
                ))}
              </div>
              <div className="rounded-2xl bg-[#FAFAF9] border border-[#F5F5F5] p-4 mb-4 text-center">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">EvalentX Employability Score</p>
                <p className="text-5xl font-extrabold text-[#0A0A0A] mt-1 tracking-tighter" style={head}>87<span className="text-lg text-[#A3A3A3]">/100</span></p>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {['Python', 'React', 'SQL', 'AI/ML', 'Node.js'].map((s) => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] font-semibold">{s}</span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Verification ID</p>
                  <p className="font-mono text-xs text-[#0A0A0A]">EVX-2048-7731</p>
                </div>
                <div className="bg-white p-1.5 rounded-lg border border-[#E5E5E5]"><QRCodeCanvas value="https://verify.evalentx.com/EVX-2048-7731" size={48} level="M" /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TRUST STRIP ================= */}
      <section className="border-b border-[#F5F5F5] bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <p className="text-center text-[11px] uppercase tracking-[0.2em] text-[#525252] font-bold mb-6">Built for the new era of skills-based hiring</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { Icon: Brain, t: 'AI-Powered Assessment' }, { Icon: ScanLine, t: 'Skill Verification' },
              { Icon: Award, t: 'Verified Credentials' }, { Icon: Search, t: 'Recruiter Discovery' },
              { Icon: Briefcase, t: 'Domestic Opportunities' }, { Icon: Globe, t: 'Global Employability' },
            ].map(({ Icon, t }) => (
              <div key={t} className="rounded-xl border border-[#F5F5F5] bg-white px-3 py-4 text-center">
                <Icon size={20} className="mx-auto text-[#B91C1C] mb-2" />
                <p className="text-xs text-[#171717] font-semibold">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROBLEM ================= */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter mb-3" style={head}>A resume can <span className="text-[#A3A3A3]">claim.</span><br className="md:hidden" /> A verified profile can <span className="text-[#B91C1C]">demonstrate.</span></h2>
          <p className="text-[#525252] max-w-2xl mx-auto">Traditional resumes are self-reported and hard to trust. A Verified Profile is evidence-backed and instantly checkable.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#E5E5E5] bg-white p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-4">Traditional Resume</p>
            <ul className="space-y-2.5">
              {['Self-reported skills', 'Static document', 'Limited evidence', 'Difficult to validate', 'Easily duplicated', 'No assessment performance', 'Hard to compare objectively'].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-[#525252]"><XIcon size={15} className="text-[#A3A3A3] flex-shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-[#B91C1C]/20 bg-[#FEF2F2] p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#B91C1C] font-bold mb-4">EvalentX Verified Profile</p>
            <ul className="space-y-2.5">
              {['Evidence-backed', 'Skill-assessed', 'AI-evaluated', 'Digitally verifiable', 'QR-enabled', 'Continuously updateable', 'Recruiter & opportunity-ready'].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-[#171717] font-medium"><Check size={15} className="text-[#B91C1C] flex-shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ================= BENEFITS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#F5F5F5]">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-center mb-3" style={head}>Why get your profile verified?</h2>
        <p className="text-center text-[#525252] mb-12 max-w-2xl mx-auto">Your professional identity, backed by evidence.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ['01', 'Stand Out Beyond Your Resume', 'Demonstrate verified capability instead of self-reported skills.'],
            ['02', 'Build Employer Trust', 'Give recruiters a structured way to understand your capabilities.'],
            ['03', 'Strengthen Every Application', 'Let your verified profile accompany every job application.'],
            ['04', 'Access Better Opportunities', 'Become discoverable for relevant domestic & global roles.'],
            ['05', 'A Professional Digital Identity', 'One profile that evolves with your career.'],
            ['06', 'A Verified Career Credential', 'A professionally designed certificate with unique verification.'],
            ['07', 'International-Standard Resume', 'Convert your verified profile into a recruiter-ready resume.'],
            ['08', 'Demonstrate Your Skills', 'Show employers what you can actually do.'],
          ].map(([n, t, d]) => (
            <div key={n} className="rounded-2xl border border-[#E5E5E5] bg-white p-5 hover:border-[#B91C1C]/40 hover:shadow-[0_16px_32px_-16px_rgba(0,0,0,0.12)] transition-all">
              <p className="text-3xl font-extrabold text-[#FECACA] mb-2 tracking-tighter" style={head}>{n}</p>
              <p className="font-bold mb-1.5 text-[#0A0A0A]">{t}</p>
              <p className="text-sm text-[#525252] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-20 border-t border-[#F5F5F5]">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-center mb-3" style={head}>From candidate to verified professional</h2>
        <p className="text-center text-[#525252] mb-12">Seven steps to your verified career identity.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            [Fingerprint, 'Create Your Profile', 'Add education, experience, skills and preferences.'],
            [FileText, 'Submit Your Resume', 'Upload an existing resume or build directly.'],
            [Brain, 'AI-Powered Assessment', 'Complete the applicable assessments and AI interview.'],
            [Zap, 'Demonstrate Your Skills', 'Answer technical, behavioural and live coding questions.'],
            [TrendingUp, 'AI Evaluation', 'Scored across multiple competency dimensions.'],
            [ShieldCheck, 'Verification Review', 'Evaluated against the applicable criteria.'],
            [Award, 'Get Verified', 'Receive your profile, credential and career documents.'],
          ].map(([Icon, t, d], i) => (
            <div key={t} className="relative rounded-2xl border border-[#E5E5E5] bg-white p-5">
              <span className="absolute top-4 right-4 text-xs font-extrabold text-[#E5E5E5]" style={head}>{String(i + 1).padStart(2, '0')}</span>
              <div className="w-10 h-10 rounded-full bg-[#FEF2F2] flex items-center justify-center mb-3"><Icon size={18} className="text-[#B91C1C]" /></div>
              <p className="font-bold mb-1 text-[#0A0A0A]">{t}</p>
              <p className="text-sm text-[#525252] leading-relaxed">{d}</p>
            </div>
          ))}
          <div className="rounded-2xl border-2 border-[#B91C1C]/25 bg-[#FEF2F2] p-5 flex flex-col justify-center items-start">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-3"><Rocket size={18} className="text-[#B91C1C]" /></div>
            <button onClick={primaryCta} className="inline-flex items-center gap-2 text-sm font-bold text-[#B91C1C]">Start now <ArrowRight size={15} /></button>
          </div>
        </div>
      </section>

      {/* ================= EMPLOYABILITY SCORE ================= */}
      <section className="max-w-6xl mx-auto px-6 py-20 border-t border-[#F5F5F5]">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter mb-3" style={head}>Know where you stand</h2>
            <p className="text-[#525252] mb-6">Your EvalentX Employability Score is a structured view of your demonstrated capabilities — designed to help you understand your strengths and development areas.</p>
            <div className="space-y-3 max-w-md">
              {[['Technical Skills', 91], ['Problem Solving', 88], ['Communication', 84], ['Professional Readiness', 86], ['Domain Knowledge', 90]].map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-[#171717] font-medium">{k}</span><span className="font-extrabold text-[#0A0A0A]">{v}</span></div>
                  <div className="h-1.5 rounded-full bg-[#F5F5F5] overflow-hidden"><div className="h-full bg-[#B91C1C]" style={{ width: `${v}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-64 h-64 rounded-full border border-[#E5E5E5] bg-[#FAFAF9] flex flex-col items-center justify-center relative">
              <div className="absolute inset-3 rounded-full border-2 border-[#B91C1C]/25" />
              <p className="text-7xl font-extrabold text-[#0A0A0A] tracking-tighter" style={head}>87</p>
              <p className="text-sm text-[#A3A3A3] font-semibold">/ 100 Employability</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= GLOBAL (India + abroad) ================= */}
      <section className="relative border-t border-[#F5F5F5] bg-[#0A0A0A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(185,28,28,0.22),_transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#FCA5A5] text-xs font-bold mb-4"><Globe size={13} /> Designed beyond borders</div>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter mb-3" style={head}>One verification.<br />Multiple interviews — across India &amp; abroad.</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">Your verified profile makes you discoverable to recruiters and unlocks guaranteed interview opportunities — at home and internationally.</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['International-standard resume', 'Professional digital profile', 'Skills-based assessment', 'Verifiable credential', 'Global-ready presentation', 'Opportunity matching'].map((t) => (
              <span key={t} className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/80">{t}</span>
            ))}
          </div>
          <button onClick={primaryCta} className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#B91C1C] hover:bg-[#7F1D1D] font-bold text-sm">
            Unlock My Interviews <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* ================= CAROUSEL ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter mb-8" style={head}>Everything you get with a Verified Profile</h2>
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x">
          {[
            [Award, 'Verified Professional Profile', 'Skills, education, projects and experience in one structured profile.'],
            [Brain, 'AI-Assessed Skills', 'Demonstrate capabilities through structured assessments and AI evaluation.'],
            [FileText, 'Recruiter-Ready Resume', 'A professionally formatted resume designed for modern hiring.'],
            [BadgeCheck, 'Verifiable Certificate', 'A digital credential with a unique verification mechanism.'],
            [QrCode, 'QR Verification', 'Recruiters scan your QR code to verify authenticity instantly.'],
            [Briefcase, 'Opportunity Access', 'Apply to relevant opportunities using your verified identity.'],
            [Globe, 'Global-Ready Profile', 'Present your capabilities in a format designed for international roles.'],
          ].map(([Icon, t, d]) => (
            <div key={t} className="snap-start flex-shrink-0 w-72 rounded-2xl border border-[#E5E5E5] bg-white p-6">
              <div className="w-11 h-11 rounded-full bg-[#FEF2F2] flex items-center justify-center mb-4"><Icon size={20} className="text-[#B91C1C]" /></div>
              <p className="font-bold mb-1.5 text-[#0A0A0A]">{t}</p>
              <p className="text-sm text-[#525252] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="plans" className="max-w-6xl mx-auto px-6 py-20 border-t border-[#F5F5F5]">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-center mb-3" style={head}>Choose your career verification track</h2>
        <p className="text-center text-[#525252] mb-3">Pass mark: <span className="text-[#0A0A0A] font-bold">{threshold}/100</span>. Prices are set by EvalentX.</p>
        {isVerified && (
          <div className="max-w-md mx-auto mb-6 text-center rounded-xl border border-[#B91C1C]/30 bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">
            You're verified! <Link to="/verified-talent/verify" className="underline font-bold">View your verified profile →</Link>
          </div>
        )}

        {/* Coupon */}
        <div className="max-w-md mx-auto mb-8">
          <div className="flex items-center gap-2">
            <input
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value)}
              placeholder="Have a coupon code?"
              className="flex-1 rounded-full border border-[#E5E5E5] px-4 py-2.5 text-sm uppercase tracking-wide focus:border-[#B91C1C] outline-none"
              data-testid="coupon-input"
            />
            {appliedCoupon ? (
              <button onClick={clearCoupon} className="px-4 py-2.5 rounded-full border border-[#E5E5E5] text-sm font-bold text-[#525252] hover:border-[#B91C1C]" data-testid="coupon-clear-btn">Remove</button>
            ) : (
              <button onClick={applyCoupon} disabled={applyingCoupon} className="px-5 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-bold disabled:opacity-50" data-testid="coupon-apply-btn">
                {applyingCoupon ? '…' : 'Apply'}
              </button>
            )}
          </div>
          {couponMsg && <p className={`mt-2 text-center text-xs font-semibold ${couponMsg.ok ? 'text-[#16803C]' : 'text-[#B91C1C]'}`} data-testid="coupon-msg">{couponMsg.text}</p>}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#B91C1C]" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {plans.map((plan) => (
              <div key={plan.id} className={`rounded-2xl border p-6 flex flex-col ${plan.is_recommended ? 'border-2 border-[#B91C1C] bg-white shadow-[0_24px_48px_-16px_rgba(185,28,28,0.20)]' : 'border-[#E5E5E5] bg-white'}`} data-testid={`plan-${plan.code}`}>
                {plan.is_recommended && <span className="self-start mb-3 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-[#B91C1C] text-white">Most popular</span>}
                <p className="font-extrabold text-lg text-[#0A0A0A]" style={head}>{plan.name}</p>
                <div className="my-3">
                  {Number(plan.price) > 0
                    ? (couponMap[plan.id]?.valid
                      ? <><span className="text-4xl font-extrabold tracking-tighter text-[#0A0A0A]" style={head}>₹{couponMap[plan.id].final_price}</span> <span className="text-[#A3A3A3] text-lg line-through">₹{plan.price}</span><span className="text-[#A3A3A3] text-sm"> / {plan.validity_months || 6} mo</span><span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#16803C]/10 text-[#16803C]" data-testid={`plan-discount-${plan.code}`}>SAVE ₹{couponMap[plan.id].discount}</span></>
                      : <><span className="text-4xl font-extrabold tracking-tighter text-[#0A0A0A]" style={head}>₹{plan.price}</span><span className="text-[#A3A3A3] text-sm"> / {plan.validity_months || 6} mo</span></>)
                    : <span className="text-3xl font-extrabold text-[#B91C1C]" style={head}>Free</span>}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#171717]"><Check size={15} className="text-[#B91C1C] flex-shrink-0 mt-0.5" /> {f}</li>
                  ))}
                  <li className="flex items-start gap-2 text-sm text-[#171717]"><Check size={15} className="text-[#B91C1C] flex-shrink-0 mt-0.5" /> {plan.attempts || 1} AI interview attempt{(plan.attempts || 1) > 1 ? 's' : ''}</li>
                </ul>
                <button onClick={() => handlePurchase(plan.id)} disabled={purchasing === plan.id} className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold disabled:opacity-60 ${plan.is_recommended ? 'bg-[#B91C1C] hover:bg-[#7F1D1D] text-white' : 'border border-[#E5E5E5] hover:border-[#B91C1C] text-[#0A0A0A]'}`} data-testid={`buy-plan-${plan.code}`}>
                  {purchasing === plan.id ? <><Loader2 size={16} className="animate-spin" /> {Number(plan.price) > 0 ? 'Creating order…' : 'Activating…'}</> : <>{Number(plan.price) > 0 ? `Get ${plan.name}` : 'Start Free Trial'} <ArrowRight size={14} /></>}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <FaqSection />

      {/* ================= FINAL CTA ================= */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center border-t border-[#F5F5F5]">
        <h2 className="text-3xl md:text-6xl font-extrabold tracking-tighter mb-4 leading-[0.95]" style={head}>
          Your resume tells them who you are.<br />
          <span className="text-[#B91C1C]">Your Verified Profile shows what you can do.</span>
        </h2>
        <p className="text-[#525252] max-w-2xl mx-auto mb-8">Unlock multiple interview opportunities across India and abroad with a trusted digital professional identity.</p>
        <button onClick={primaryCta} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#B91C1C] hover:bg-[#7F1D1D] text-white font-bold" data-testid="final-cta-btn">
          Build Your Verified Career Profile <ArrowRight size={18} />
        </button>
        <p className="mt-4 text-sm text-[#A3A3A3] font-semibold">Get Verified → Get Discovered → Get Hired</p>
      </section>

      {hasActivePlan && <ExtraAttemptsSection />}
      <footer className="py-8 text-center text-xs text-[#A3A3A3] border-t border-[#F5F5F5]">EvalentX Verified Profile · Powered by Athena AI</footer>
    </div>
  );
};

const FAQS = [
  ['What is an EvalentX Verified Profile?', 'A digitally verifiable career profile that combines your information, demonstrated skills, AI assessment performance and employability indicators into one trusted professional identity.'],
  ['Why should I get my profile verified?', 'It lets you demonstrate verified capability rather than self-reported skills, builds recruiter trust, and unlocks multiple interview opportunities across India and abroad.'],
  ['How does the verification process work?', 'Create your profile, submit a resume, complete an AI-powered interview with live coding and MCQs, get evaluated across competency dimensions, then receive your verified profile and credential.'],
  ['Is the verification valid for job applications?', 'Yes — your verified profile can accompany your applications and make you discoverable for relevant domestic and global opportunities.'],
  ['Can I apply for jobs without verification?', 'Yes. Verification strengthens your application, but you can still continue without it unless a specific employer requires it.'],
  ['Can I download a resume and certificate after verification?', 'Yes — you receive a recruiter-ready resume and a verifiable digital certificate with a QR code.'],
  ['How does the QR verification work?', 'Each credential has a unique verification ID and QR code that opens a public verification page confirming authenticity.'],
  ['How long is my verification valid?', 'Validity depends on your plan (typically 6 months). You can re-assess to refresh your profile.'],
  ['What happens if I do not pass verification?', 'You receive a personalised growth report with your strengths and focus areas, and can retry with your remaining attempts.'],
];

const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-3xl mx-auto px-6 py-20 border-t border-[#F5F5F5]" data-testid="faq-section">
      <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-center mb-10" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Frequently asked questions</h2>
      <div className="space-y-3">
        {FAQS.map(([q, a], i) => (
          <div key={i} className="rounded-xl border border-[#E5E5E5] bg-white overflow-hidden">
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left" data-testid={`faq-${i}`}>
              <span className="font-bold text-sm text-[#0A0A0A]">{q}</span>
              <ChevronDown size={17} className={`text-[#A3A3A3] flex-shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
            </button>
            {open === i && <div className="px-5 pb-4 text-sm text-[#525252] leading-relaxed">{a}</div>}
          </div>
        ))}
      </div>
    </section>
  );
};

export default VerifiedTalentLandingPage;

const ExtraAttemptsSection = () => {
  const [offers, setOffers] = useState([]);
  const [buying, setBuying] = useState(null);
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    axios.get(`${API}/api/verified-talent/config`).then(res => setOffers(res.data?.extra_attempt_prices || [])).catch(() => {});
  }, []);

  const loadCashfreeSdk = () => new Promise((resolve) => {
    if (window.Cashfree) return resolve();
    const s = document.createElement('script'); s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js'; s.onload = resolve; document.body.appendChild(s);
  });

  const buy = async (index) => {
    setBuying(index);
    try {
      const res = await axios.post(`${API}/api/verified-talent/extra-attempts/purchase`, { package_index: index }, { headers: { Authorization: `Bearer ${token}` } });
      const { payment_session_id, cashfree_env, order_id } = res.data || {};
      if (!payment_session_id) { toast.error('Payment session failed'); setBuying(null); return; }
      await loadCashfreeSdk();
      // eslint-disable-next-line no-undef
      const cashfree = window.Cashfree({ mode: cashfree_env === 'sandbox' ? 'sandbox' : 'production' });
      cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: '_self', returnUrl: `${window.location.origin}/verified-talent/verify?extra_order_id=${order_id}` });
    } catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); setBuying(null); }
  };

  if (offers.length === 0) return null;
  return (
    <section className="py-14 border-t border-[#F5F5F5] bg-[#FAFAF9]" data-testid="extra-attempts-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#B91C1C] font-bold mb-2">Need more attempts?</p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Top up without buying a new plan</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {offers.map((o, i) => (
            <div key={i} className="p-5 bg-white border border-[#E5E5E5] rounded-xl flex items-center justify-between">
              <div><p className="font-bold text-lg text-[#0A0A0A]">{o.label}</p><p className="text-xs text-[#525252]">+{o.attempts} AI interview attempt{o.attempts > 1 ? 's' : ''}</p></div>
              <div className="text-right">
                <p className="text-2xl font-extrabold text-[#B91C1C] mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>₹{o.price}</p>
                <button onClick={() => buy(i)} disabled={buying !== null} className="text-xs font-bold px-3 py-1.5 bg-[#B91C1C] hover:bg-[#7F1D1D] text-white rounded-full disabled:opacity-50" data-testid={`landing-extra-attempt-${i}`}>{buying === i ? 'Loading…' : 'Buy now'}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
